import { cn } from '@/lib/utils'
import type { Turn } from '@/stores/useFStore'
import { FThinkingBlock } from './FThinkingBlock'
import ReactMarkdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import type { ComponentPropsWithoutRef } from 'react'

interface FChatMessageProps {
  turn: Turn
  isStreaming?: boolean
}

export function FChatMessage({ turn, isStreaming }: FChatMessageProps) {
  const isUser = turn.role === 'user'
  const navigate = useNavigate()

  // Custom link component that handles internal navigation
  const MarkdownLink = ({ href, children, ...props }: ComponentPropsWithoutRef<'a'>) => {
    const isInternal = href?.startsWith('/')

    if (isInternal && href) {
      return (
        <a
          {...props}
          href={href}
          onClick={(e) => {
            e.preventDefault()
            navigate(href)
          }}
          className="text-foreground underline underline-offset-2 hover:text-foreground/80 transition-colors cursor-pointer"
        >
          {children}
        </a>
      )
    }

    return (
      <a href={href} {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  }

  return (
    <div
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%]',
          isUser
            ? 'rounded-lg px-4 py-3 bg-foreground text-background'
            : 'py-3'
        )}
      >
        {/* Per-node thinking blocks for assistant messages */}
        {!isUser && turn.evaluatorRan && turn.evaluatorThinking && (
          <FThinkingBlock
            thinking={turn.evaluatorThinking}
            isStreaming={false}
            label="Evaluator"
            color="text-green-600"
          />
        )}
        {!isUser && turn.editorRan && turn.editorThinking && (
          <FThinkingBlock
            thinking={turn.editorThinking}
            isStreaming={false}
            label="Editor"
            color="text-green-600"
          />
        )}
        {!isUser && turn.responderRan && turn.responderThinking && (
          <FThinkingBlock
            thinking={turn.responderThinking}
            isStreaming={false}
            label="Responder"
            color="text-green-600"
          />
        )}
        {/* Fallback: legacy single thinking block */}
        {!isUser && turn.thinking && !turn.evaluatorThinking && !turn.editorThinking && !turn.responderThinking && (
          <FThinkingBlock thinking={turn.thinking} isStreaming={false} />
        )}

        {/* Response content */}
        {isUser ? (
          <div className="prose-chat-user text-sm max-w-none [&_*:last-child]:mb-0 [&_p]:mb-2">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        ) : (
          <div className="prose-chat text-sm min-h-0 [&_*:last-child]:mb-0">
            <ReactMarkdown components={{ a: MarkdownLink }}>{turn.content.trim()}</ReactMarkdown>
            {/* Streaming cursor */}
            {isStreaming && (
              <span className="inline-block w-1 h-4 bg-foreground/30 animate-pulse ml-0.5" />
            )}
          </div>
        )}

        {/* Cards integrated indicator */}
        {!isUser && turn.cardsUsed?.integrated && turn.cardsUsed.integrated.length > 0 && (
          <div className="text-[10px] mt-1.5 font-light tracking-wide text-muted-foreground/70">
            {turn.cardsUsed.integrated.length} card(s) integrated
          </div>
        )}

        <div
          className={cn(
            'text-[10px] mt-1.5 font-light tracking-wide',
            isUser ? 'text-background/50' : 'text-muted-foreground/70'
          )}
        >
          {new Date(turn.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  )
}
